import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Braces, ChevronDown, ChevronUp, GripVertical, Link2, Search } from 'lucide-react';

// Inputs that accept {{tokens}} carry this attribute. The editor remembers
// the last one focused so a click on a variable knows where to insert, and
// the CSS in index.css highlights them while a variable is being dragged.
export const TEMPLATE_FIELD_ATTR = 'data-template-field';

// Keep in sync with interpolate() in server/services/automationEngine.js.
export const LEAD_VARIABLES = [
  { token: 'firstName', label: 'First name' },
  { token: 'lastName', label: 'Last name' },
  { token: 'fullName', label: 'Full name' },
  { token: 'email', label: 'Email' },
  { token: 'phone', label: 'Phone' },
  { token: 'company', label: 'Company' },
  { token: 'position', label: 'Position' },
  { token: 'source', label: 'Source' },
  { token: 'status', label: 'Status' },
  { token: 'priority', label: 'Priority' },
  { token: 'estimatedValue', label: 'Deal value' },
  { token: 'leadUrl', label: 'Link to lead', hint: 'Opens the lead in the CRM. Best for team emails and notifications.', icon: Link2 },
];

export const TASK_VARIABLES = [
  { token: 'taskTitle', label: 'Task title' },
  { token: 'taskDescription', label: 'Task description' },
  { token: 'taskType', label: 'Task type' },
  { token: 'taskPriority', label: 'Task priority' },
  { token: 'taskDueDate', label: 'Due date and time' },
  { token: 'taskDueDateShort', label: 'Due date' },
];

// Insert text into a React-controlled input/textarea at its caret (or over
// its selection). Going through the native value setter + an input event
// makes React's onChange fire as if the user typed it.
export function insertAtCursor(el, text) {
  const value = el.value || '';
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? start;
  const next = value.slice(0, start) + text + value.slice(end);
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, next);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.focus();
  const caret = start + text.length;
  el.setSelectionRange(caret, caret);
}

const VariableChip = ({ variable, onInsert, onDragState }) => {
  const Icon = variable.icon;
  const text = `{{${variable.token}}}`;
  // A div, not a <button>: Firefox never starts a drag on a button. And no
  // preventDefault on mousedown, that cancels the drag in every browser.
  // The field keeps its selection on blur and insertAtCursor refocuses it.
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      title={variable.hint || `Drag into a message, or click to insert ${text}`}
      onClick={() => onInsert(text)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInsert(text); }
      }}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', text);
        e.dataTransfer.effectAllowed = 'copy';
        onDragState(true);
      }}
      onDragEnd={() => onDragState(false)}
      className="group w-full flex items-center gap-1.5 px-2 py-1.5 text-left rounded-md border border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50 cursor-grab active:cursor-grabbing select-none"
    >
      <GripVertical size={12} className="text-gray-300 group-hover:text-primary-400 flex-shrink-0 hidden lg:block" />
      {Icon && <Icon size={12} className="text-primary-600 flex-shrink-0" />}
      <span className="flex-1 min-w-0">
        <span className="block text-xs text-gray-800 truncate">{variable.label}</span>
        <code className="block text-[10px] text-gray-500 truncate">{text}</code>
      </span>
    </div>
  );
};

const Group = ({ title, variables, onInsert, onDragState }) => {
  if (variables.length === 0) return null;
  return (
    <div>
      <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{title}</h4>
      <div className="grid grid-cols-2 lg:grid-cols-1 gap-1.5">
        {variables.map((v) => (
          <VariableChip key={v.token} variable={v} onInsert={onInsert} onDragState={onDragState} />
        ))}
      </div>
    </div>
  );
};

// Variable picker for automation message templates. Desktop: a sticky side
// panel, drag a variable onto any message field. Touch screens can't drag,
// so a tap inserts at the caret of the last message field the user touched.
const TemplateVariablesPanel = ({ customFields = [], showTaskVariables = false, targetRef, onDragState = () => {}, collapsible = false }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(!collapsible);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (v) => !q || v.label.toLowerCase().includes(q) || v.token.toLowerCase().includes(q);
    const cf = customFields
      .filter((f) => f?.key)
      .map((f) => ({ token: `lead.cf.${f.key}`, label: f.label || f.key }));
    return [
      { title: 'Lead', variables: LEAD_VARIABLES.filter(match) },
      { title: 'Task', variables: showTaskVariables ? TASK_VARIABLES.filter(match) : [] },
      { title: 'Custom fields', variables: cf.filter(match) },
    ];
  }, [query, customFields, showTaskVariables]);

  const insert = (text) => {
    const el = targetRef?.current;
    if (!el || !el.isConnected) {
      toast('Click into a message field first, then pick a variable.', { icon: '👆' });
      return;
    }
    insertAtCursor(el, text);
  };

  const empty = groups.every((g) => g.variables.length === 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <button
        type="button"
        onClick={() => collapsible && setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${collapsible ? '' : 'cursor-default'}`}
        aria-expanded={open}
      >
        <Braces size={14} className="text-primary-600" />
        <span className="flex-1 text-sm font-semibold text-gray-900">Variables</span>
        {collapsible && (open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />)}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-[11px] text-gray-500 leading-snug">
            <span className="hidden lg:inline">Drag a variable into a message, or click a message field and then a variable.</span>
            <span className="lg:hidden">Tap a message field, then tap a variable to insert it.</span>
          </p>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search variables"
              className="w-full pl-6 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {groups.map((g) => (
            <Group key={g.title} title={g.title} variables={g.variables} onInsert={insert} onDragState={onDragState} />
          ))}
          {empty && <p className="text-xs text-gray-400 italic">No variables match.</p>}
          <p className="text-[10px] text-gray-400 leading-snug">
            Empty values render as blank.
            {showTaskVariables && <> Task custom fields: <code>{'{{task.cf.<key>}}'}</code></>}
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateVariablesPanel;
